/**
 * The deep-link contract between the admin command palette and the pages it opens controls on.
 * The palette builds every action href here, and each destination page reads the same `action`
 * ids back with `useActionParam`, so the two sides cannot drift. Link actions are plain hrefs no
 * page reads back. Actions never write: the page's own control does, with its own auth, confirm
 * and toast. The ids, params, anchors and subjects live in `admin-action-definitions.ts`; this
 * module re-exports them next to {@link adminActionLink}, so importers use this path only.
 */

import {
  ADMIN_ALL_ORGS_USERS_PATH,
  ADMIN_EVENTS_PATH,
  ADMIN_FINANCES_PATH,
  ADMIN_ORGS_PATH,
  type AdminEventFinancesTab,
  type AdminOrgSettingsTab,
  type AdminRegistrationTab,
  adminCreateEventHref,
  adminCreateFreshEventHref,
  adminEventContractPath,
  adminEventDetailsPath,
  adminEventLiveLandingPagePath,
  adminEventFinancesPath,
  adminEventNotesPath,
  adminEventProposalsComparePath,
  adminEventRegistrationPath,
  adminEventSourcePath,
  adminFinancesInvoicePath,
  adminFinancesPath,
  adminMetricsPath,
  adminOrgEventsPath,
  adminOrgSettingsPath,
  adminOrgUsersPath,
  adminUserPath,
} from "@/lib/routes/admin-dash-paths";
import { AVA_REVIEW_QUOTES_PATH, avaAddQuotePath } from "@/lib/routes/ava-paths";
import { DASHBOARD_PATH } from "@/lib/routes/enterprise-nav-paths";

import {
  type AdminPageAction,
  type AdminLinkAction,
  type AdminLandingAction,
  ACTION_PARAM,
  OWNERSHIP_ANCHOR,
  USERS_ANCHOR,
  VENUE_PARAM,
  OFFER_PARAM,
  USER_PARAM,
  ORG_PARAM,
  GROUP_PARAM,
  EVENT_STATUS_ANCHOR,
  type EventActionSubject,
  type EventDetailsActionSubject,
  type EventFinancesTabActionSubject,
  type VenueActionSubject,
  type VenueQuoteActionSubject,
  type ContractActionSubject,
  type ProposalCompareActionSubject,
  type VenueOverlayTabActionSubject,
  type OrgActionSubject,
  type UserActionSubject,
  type UserGroupActionSubject,
  type UserOptionalOrgActionSubject,
  type AdminActionSubjects,
} from "@/lib/routes/admin-action-definitions";

export * from "@/lib/routes/admin-action-definitions";

/** A canonical 8-4-4-4-12 hex UUID, the shape of an offer id. */
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Appends `action=<id>` to a path that may already carry a query string.
 *
 * @param path - The destination path, with or without `?`.
 * @param action - The action id.
 * @returns The path with the action param.
 */
function withAction(path: string, action: AdminLandingAction): string {
  const separator = path.includes("?") ? "&" : "?";
  return `${path}${separator}${ACTION_PARAM}=${action}`;
}

/**
 * Throws unless every id is a positive safe integer, so a tampered or malformed id can never
 * build a link to some other record.
 *
 * @param ids - The subject's ids.
 * @throws When any id is not a positive safe integer.
 */
function assertIds(...ids: number[]): void {
  if (ids.some((id) => !Number.isSafeInteger(id) || id <= 0)) {
    throw new Error("adminActionLink: ids must be positive safe integers");
  }
}

/** The Finances tab each tab-bound finances action lands on. */
const EVENT_FINANCES_ACTION_TABS = {
  "close-books": "overview",
  "add-charge": "charges-card",
  "upload-service-contract": "service-contracts",
  "change-event-fee": "overview",
  "reopen-books": "overview",
  "open-credit-card": "charges-card",
  "create-payment-link": "charges-card",
  "enter-contract-terms": "accounts-receivable",
  "enter-folio-amounts": "folio-reconciliation",
  "categorize-wires": "cash-flows",
  "show-all-documents": "documents",
} as const satisfies Partial<Record<AdminLandingAction, AdminEventFinancesTab>>;

/** The Registration tab each registration action lands on. */
const EVENT_REGISTRATION_ACTION_TABS = {
  "create-activity": "activities",
  "add-schedule-item": "schedule",
  "create-email-template": "notifications",
  "generate-landing-page": "landing-pages",
  "duplicate-landing-page": "landing-pages",
  "create-hotel-booking-link": "forms",
  "generate-feedback-form": "forms",
  "duplicate-form": "forms",
  "open-activity-settings": "activities",
  "open-signup-limits": "activities",
  "open-calendar-sync": "schedule",
  "open-schedule-settings": "schedule",
  "open-comms-history": "notifications",
  "open-text-templates": "notifications",
  "open-comms-settings": "notifications",
  "open-scheduled-sends": "notifications",
} as const satisfies Partial<Record<AdminLandingAction, AdminRegistrationTab>>;

/** The organization Settings tab each settings action lands on. */
const ORG_SETTINGS_ACTION_TABS = {
  "add-policy": "policies",
  "add-preferred-venue": "preferred-venues",
  "add-blacklisted-venue": "blacklisted-venues",
  "add-contract-clause": "contracts",
  "add-contract-template": "contracts",
  "rename-org": "profile",
  "change-org-address": "profile",
  "change-org-industry": "profile",
  "change-org-star-rating": "profile",
  "edit-org-deposit": "profile",
  "change-org-sourcing-fee": "profile",
  "reserve-subdomain": "profile",
  "upload-org-logo": "profile",
  "prioritize-office-venues": "office-location",
  "import-preferred-venues": "preferred-venues",
  "add-preferred-chain": "preferred-venues",
  "add-preferred-vendor": "preferred-venues",
  "add-blacklisted-chain": "blacklisted-venues",
} as const satisfies Partial<Record<AdminLandingAction, AdminOrgSettingsTab>>;

/**
 * Where each global landing action opens: the all-organizations or tool page that has its control.
 * Built on call, never at import, so a test that mocks the path module in part can still load this.
 */
const GLOBAL_ACTION_PATHS = {
  "show-my-events": () => ADMIN_EVENTS_PATH,
  "show-unclaimed-events": () => ADMIN_EVENTS_PATH,
  "create-user": () => ADMIN_ALL_ORGS_USERS_PATH,
  "show-my-orgs": () => ADMIN_ORGS_PATH,
  "categorize-revenue-sources": () => adminMetricsPath("finance"),
  "review-payment-links": () => adminMetricsPath("finance"),
  "show-outstanding": () => adminMetricsPath("finance"),
  "add-deal": () => adminMetricsPath("pipeline"),
  "show-unlinked-deals": () => adminMetricsPath("pipeline"),
  "show-events-needing-closing": () => adminFinancesPath("accounting"),
  "show-events-needing-commission": () => adminFinancesPath("accounting"),
  "show-events-needing-folio": () => adminFinancesPath("accounting"),
  "add-journal-entry": () => adminFinancesPath("metrics-2"),
  "create-subscription-contract": () => `${ADMIN_FINANCES_PATH}/subscription-contracts`,
} as const satisfies Partial<Record<AdminLandingAction, () => string>>;

/** Each global link action's href: a tool page with the view its own URL params pick. Built on call. */
const GLOBAL_LINK_HREFS = {
  "show-last-month": () => `${adminMetricsPath("fetch")}?preset=last_month`,
  "show-last-3-months": () => `${adminMetricsPath("fetch")}?preset=last_3_months`,
  "show-attention-threads": () => `${adminMetricsPath("planner-bot")}?view=attention`,
  "show-failing-threads": () => `${adminMetricsPath("planner-bot")}?view=failing`,
  "show-action-threads": () => `${adminMetricsPath("planner-bot")}?view=actions`,
  "show-wire-transfers": () => `${adminFinancesPath("reconciliation")}?tab=wires`,
  "show-card-expenses": () => `${adminFinancesPath("reconciliation")}?tab=expenses`,
  "show-reconciliation-needs-closing": () => `${adminFinancesPath("reconciliation")}?tab=events&subTab=needs_closing`,
  "show-commission-issues": () => `${adminFinancesPath("reconciliation")}?tab=events&subTab=needs_commission`,
} as const satisfies Partial<Record<AdminLinkAction, () => string>>;

/** Each event Source landing's path builder. */
const EVENT_SOURCE_ACTION_PATHS = {
  "email-partners": (eventId: number) => adminEventSourcePath(eventId, "venues"),
  "show-quoted-venues": (eventId: number) => adminEventSourcePath(eventId, "venues"),
  "show-quiet-venues": (eventId: number) => adminEventSourcePath(eventId, "venues"),
  "fix-venue-photos": (eventId: number) => adminEventSourcePath(eventId, "venues"),
  "show-bookmarked-proposals": (eventId: number) => adminEventSourcePath(eventId, "proposals"),
  "show-reviewed-contracts": (eventId: number) => adminEventSourcePath(eventId, "contracts"),
  "select-rfp-batch": (eventId: number) => `${adminEventSourcePath(eventId, "search")}/hotel`,
  "search-location": (eventId: number) => `${adminEventSourcePath(eventId, "search")}/hotel`,
  "show-bookmarked-search": (eventId: number) => `${adminEventSourcePath(eventId, "search")}/hotel`,
  "show-venues-missing-email": (eventId: number) => adminEventSourcePath(eventId, "venues"),
  "import-venue-list": (eventId: number) => adminEventSourcePath(eventId, "venues"),
  "show-bookmarked-venues": (eventId: number) => adminEventSourcePath(eventId, "venues"),
  "show-archived-venues": (eventId: number) => adminEventSourcePath(eventId, "venues"),
  "show-booked-venues": (eventId: number) => adminEventSourcePath(eventId, "venues"),
  "show-declined-venues": (eventId: number) => adminEventSourcePath(eventId, "venues"),
  "show-unavailable-venues": (eventId: number) => adminEventSourcePath(eventId, "venues"),
  "show-not-contacted-venues": (eventId: number) => adminEventSourcePath(eventId, "venues"),
  "show-rfp-sent-venues": (eventId: number) => adminEventSourcePath(eventId, "venues"),
  "email-hotel-reps": (eventId: number) => adminEventSourcePath(eventId, "venues"),
  "email-destination-partners": (eventId: number) => adminEventSourcePath(eventId, "venues"),
  "show-packages": (eventId: number) => adminEventSourcePath(eventId, "proposals"),
  "create-package": (eventId: number) => adminEventSourcePath(eventId, "proposals"),
  "show-awaiting-proposals": (eventId: number) => adminEventSourcePath(eventId, "proposals"),
  "show-declined-proposals": (eventId: number) => adminEventSourcePath(eventId, "proposals"),
  "show-unavailable-proposals": (eventId: number) => adminEventSourcePath(eventId, "proposals"),
  "show-quoted-search": (eventId: number) => `${adminEventSourcePath(eventId, "search")}/hotel`,
  "open-search-filters": (eventId: number) => `${adminEventSourcePath(eventId, "search")}/hotel`,
  "set-travel-origin": (eventId: number) => `${adminEventSourcePath(eventId, "search")}/hotel`,
  "select-bookmarked-rfp-batch": (eventId: number) => `${adminEventSourcePath(eventId, "search")}/hotel`,
  "change-search-dates": (eventId: number) => `${adminEventSourcePath(eventId, "search")}/hotel`,
  "show-preferred-search": (eventId: number) => `${adminEventSourcePath(eventId, "search")}/hotel`,
} as const satisfies Partial<Record<AdminLandingAction, (eventId: number) => string>>;

/** The Finances tab each typed upload opens on when the caller names none. */
const EVENT_FINANCES_UPLOAD_TABS = {
  "upload-signed-contract": "accounts-receivable",
  "upload-final-folio": "folio-reconciliation",
} as const satisfies Partial<Record<AdminLandingAction, AdminEventFinancesTab>>;

/**
 * The path that opens one venue's detail overlay on an event's Source tab (`?venue=<id>`, read by
 * `useVenueSelection` on both the venues tab and the Proposals page).
 *
 * @param tab - The Source tab the overlay opens on.
 * @param subject - The venue and the event it is on.
 * @returns The tab path with the venue param.
 * @throws When either id is not a positive safe integer.
 */
function venueOverlayPath(tab: "venues" | "proposals", { venueId, eventId }: VenueActionSubject): string {
  assertIds(venueId, eventId);
  return `${adminEventSourcePath(eventId, tab)}?${VENUE_PARAM}=${venueId}`;
}

/**
 * The href that lands on an action's page with its control ready to open.
 *
 * @param actionId - The action id.
 * @param subject - The record the action applies to (an empty object for a global action).
 * @returns The destination href. Landing actions carry `action=<id>`; link actions are plain hrefs.
 * @throws When the subject's ids are not positive safe integers.
 */
export function adminActionLink<A extends AdminPageAction>(actionId: A, subject: AdminActionSubjects[A]): string {
  // Widened to the union so each case narrows it (a generic parameter does not narrow).
  const action: AdminPageAction = actionId;
  switch (action) {
    case "transfer-owner":
    case "change-org":
    case "set-status":
    case "claim-event":
    case "set-priority":
    case "assign-poc":
    case "assign-va": {
      const { eventId } = subject as EventActionSubject;
      assertIds(eventId);
      return `${withAction(adminEventDetailsPath(eventId), action)}#${OWNERSHIP_ANCHOR}`;
    }
    case "add-venues":
    case "send-rfps":
    case "send-goodbyes": {
      const { eventId } = subject as EventActionSubject;
      assertIds(eventId);
      return withAction(adminEventSourcePath(eventId, "venues"), action);
    }
    case "open-venue":
    case "mark-venue-unavailable":
    case "mark-venue-booked":
    case "email-venue":
    case "send-venue-rfp":
    case "mark-venue-available":
    case "unmark-venue-booked":
    case "archive-venue":
    case "unarchive-venue": {
      return withAction(venueOverlayPath("venues", subject as VenueActionSubject), action);
    }
    case "view-quote": {
      const { quoteGroupId, ...venue } = subject as VenueQuoteActionSubject;
      const path = venueOverlayPath("venues", venue);
      if (quoteGroupId === undefined) return withAction(path, action);
      if (!UUID_PATTERN.test(quoteGroupId)) throw new Error("adminActionLink: offer id must be a UUID");
      return withAction(`${path}&${OFFER_PARAM}=${quoteGroupId}`, action);
    }
    case "open-proposals":
    case "share-proposals": {
      const { eventId } = subject as EventActionSubject;
      assertIds(eventId);
      return withAction(adminEventSourcePath(eventId, "proposals"), action);
    }
    case "negotiate-proposal":
    case "request-contract":
    case "decline-proposal": {
      return withAction(venueOverlayPath("proposals", subject as VenueActionSubject), action);
    }
    case "invite-user": {
      const { orgId } = subject as OrgActionSubject;
      assertIds(orgId);
      return withAction(adminOrgUsersPath(orgId), action);
    }
    case "grant-all-events": {
      const { userId, orgId } = subject as UserActionSubject;
      assertIds(userId, orgId);
      return withAction(`${adminOrgUsersPath(orgId)}?${USER_PARAM}=${userId}`, action);
    }
    case "add-events-to-user": {
      const { userId, orgId, groupId } = subject as UserGroupActionSubject;
      assertIds(userId, orgId, ...(groupId === undefined ? [] : [groupId]));
      const groupQuery = groupId === undefined ? "" : `&${GROUP_PARAM}=${groupId}`;
      return withAction(`${adminUserPath(userId)}?${ORG_PARAM}=${orgId}${groupQuery}`, action);
    }
    case "create-invoice": {
      const { eventId } = subject as EventActionSubject;
      assertIds(eventId);
      return withAction(adminFinancesInvoicePath(eventId), action);
    }
    case "edit-event-details": {
      const { eventId, section } = subject as EventDetailsActionSubject;
      assertIds(eventId);
      const hash = section === undefined ? "" : `#${section}`;
      return `${withAction(adminEventDetailsPath(eventId), action)}${hash}`;
    }
    case "open-landing-pages":
    case "create-landing-page": {
      const { eventId } = subject as EventActionSubject;
      assertIds(eventId);
      return withAction(adminEventRegistrationPath(eventId, "landing-pages"), action);
    }
    case "open-forms":
    case "create-form": {
      const { eventId } = subject as EventActionSubject;
      assertIds(eventId);
      return withAction(adminEventRegistrationPath(eventId, "forms"), action);
    }
    case "new-note": {
      const { eventId } = subject as EventActionSubject;
      assertIds(eventId);
      return withAction(adminEventNotesPath(eventId), action);
    }
    case "upload-event-documents": {
      // The upload picker is mounted on every Finances tab, so it opens on the tab given.
      const { eventId, tab = "documents" } = subject as EventFinancesTabActionSubject;
      assertIds(eventId);
      return withAction(adminEventFinancesPath(eventId, tab), action);
    }
    case "open-ledger": {
      // The ledger button is in the utility row every Finances tab shares.
      const { eventId, tab = "overview" } = subject as EventFinancesTabActionSubject;
      assertIds(eventId);
      return withAction(adminEventFinancesPath(eventId, tab), action);
    }
    case "close-books":
    case "add-charge":
    case "upload-service-contract":
    case "change-event-fee":
    case "reopen-books":
    case "open-credit-card":
    case "create-payment-link":
    case "enter-contract-terms":
    case "enter-folio-amounts":
    case "categorize-wires":
    case "show-all-documents": {
      const { eventId } = subject as EventActionSubject;
      assertIds(eventId);
      return withAction(adminEventFinancesPath(eventId, EVENT_FINANCES_ACTION_TABS[action]), action);
    }
    case "create-activity":
    case "add-schedule-item":
    case "create-email-template":
    case "generate-landing-page":
    case "duplicate-landing-page":
    case "create-hotel-booking-link":
    case "generate-feedback-form":
    case "duplicate-form":
    case "open-activity-settings":
    case "open-signup-limits":
    case "open-calendar-sync":
    case "open-schedule-settings":
    case "open-comms-history":
    case "open-text-templates":
    case "open-comms-settings":
    case "open-scheduled-sends": {
      const { eventId } = subject as EventActionSubject;
      assertIds(eventId);
      return withAction(adminEventRegistrationPath(eventId, EVENT_REGISTRATION_ACTION_TABS[action]), action);
    }
    case "upload-contract": {
      const { eventId } = subject as EventActionSubject;
      assertIds(eventId);
      return withAction(adminEventSourcePath(eventId, "contracts"), action);
    }
    case "add-policy":
    case "add-preferred-venue":
    case "add-blacklisted-venue":
    case "add-contract-clause":
    case "add-contract-template":
    case "rename-org":
    case "change-org-address":
    case "change-org-industry":
    case "change-org-star-rating":
    case "edit-org-deposit":
    case "change-org-sourcing-fee":
    case "reserve-subdomain":
    case "upload-org-logo":
    case "prioritize-office-venues":
    case "import-preferred-venues":
    case "add-preferred-chain":
    case "add-preferred-vendor":
    case "add-blacklisted-chain": {
      const { orgId } = subject as OrgActionSubject;
      assertIds(orgId);
      return withAction(adminOrgSettingsPath(orgId, ORG_SETTINGS_ACTION_TABS[action]), action);
    }
    case "cancel-event":
    case "reopen-event": {
      const { eventId } = subject as EventActionSubject;
      assertIds(eventId);
      return withAction(adminEventDetailsPath(eventId), action);
    }
    case "edit-org-details": {
      const { orgId } = subject as OrgActionSubject;
      assertIds(orgId);
      return withAction(adminOrgSettingsPath(orgId, "profile"), action);
    }
    case "create-group":
    case "show-org-groups": {
      const { orgId } = subject as OrgActionSubject;
      assertIds(orgId);
      return withAction(adminOrgUsersPath(orgId), action);
    }
    case "create-org-event": {
      // Straight into the create flow for this organization; Duplicate an event is its own row.
      const { orgId } = subject as OrgActionSubject;
      assertIds(orgId);
      return adminCreateFreshEventHref(orgId);
    }
    case "duplicate-event":
    case "show-cancelled-events": {
      const { orgId } = subject as OrgActionSubject;
      assertIds(orgId);
      return withAction(adminOrgEventsPath(orgId), action);
    }
    case "new-organization":
      return withAction(ADMIN_ORGS_PATH, action);
    case "add-user-to-group": {
      const { userId, orgId } = subject as UserOptionalOrgActionSubject;
      assertIds(userId, ...(orgId === undefined ? [] : [orgId]));
      const orgQuery = orgId === undefined ? "" : `?${ORG_PARAM}=${orgId}`;
      return withAction(`${adminUserPath(userId)}${orgQuery}`, action);
    }
    case "manage-event-access": {
      const { eventId } = subject as EventActionSubject;
      assertIds(eventId);
      return `${withAction(adminEventDetailsPath(eventId), action)}#${USERS_ANCHOR}`;
    }
    case "view-live-landing-page": {
      const { eventId } = subject as EventActionSubject;
      assertIds(eventId);
      return adminEventLiveLandingPagePath(eventId);
    }
    case "view-as-user":
    case "edit-user-profile": {
      // The impersonation confirm needs no organization, so an `orgId` the palette passes is dropped.
      const { userId } = subject as UserOptionalOrgActionSubject;
      assertIds(userId);
      return withAction(adminUserPath(userId), action);
    }
    case "open-my-client-dashboard":
      // The signed-in admin's own customer dashboard, never a client's.
      return DASHBOARD_PATH;
    case "enter-quote": {
      const { venueId, eventId } = subject as VenueActionSubject;
      assertIds(venueId, eventId);
      return avaAddQuotePath(eventId, venueId);
    }
    case "review-quotes":
      return AVA_REVIEW_QUOTES_PATH;
    case "create-event":
      // No organization in scope: the create flow asks for one.
      return adminCreateEventHref(null);
    case "show-my-events":
    case "show-unclaimed-events":
    case "create-user":
    case "show-my-orgs":
    case "categorize-revenue-sources":
    case "review-payment-links":
    case "show-outstanding":
    case "add-deal":
    case "show-unlinked-deals":
    case "show-events-needing-closing":
    case "show-events-needing-commission":
    case "show-events-needing-folio":
    case "add-journal-entry":
    case "create-subscription-contract":
      return withAction(GLOBAL_ACTION_PATHS[action](), action);
    case "show-last-month":
    case "show-last-3-months":
    case "show-attention-threads":
    case "show-failing-threads":
    case "show-action-threads":
    case "show-wire-transfers":
    case "show-card-expenses":
    case "show-reconciliation-needs-closing":
    case "show-commission-issues":
      return GLOBAL_LINK_HREFS[action]();
    case "rename-event":
    case "change-venue-facing-name":
    case "update-hotels": {
      const { eventId } = subject as EventActionSubject;
      assertIds(eventId);
      return withAction(adminEventDetailsPath(eventId), action);
    }
    case "open-agenda-planner": {
      const { eventId } = subject as EventActionSubject;
      assertIds(eventId);
      return `${withAction(adminEventDetailsPath(eventId), action)}#agenda`;
    }
    case "resend-rfps": {
      const { eventId } = subject as EventActionSubject;
      assertIds(eventId);
      return `${withAction(adminEventDetailsPath(eventId), action)}#${EVENT_STATUS_ANCHOR}`;
    }
    case "search-notes": {
      const { eventId } = subject as EventActionSubject;
      assertIds(eventId);
      return withAction(adminEventNotesPath(eventId), action);
    }
    case "email-partners":
    case "show-quoted-venues":
    case "show-quiet-venues":
    case "fix-venue-photos":
    case "show-bookmarked-proposals":
    case "show-reviewed-contracts":
    case "select-rfp-batch":
    case "search-location":
    case "show-bookmarked-search":
    case "show-venues-missing-email":
    case "import-venue-list":
    case "show-bookmarked-venues":
    case "show-archived-venues":
    case "show-booked-venues":
    case "show-declined-venues":
    case "show-unavailable-venues":
    case "show-not-contacted-venues":
    case "show-rfp-sent-venues":
    case "email-hotel-reps":
    case "email-destination-partners":
    case "show-packages":
    case "create-package":
    case "show-awaiting-proposals":
    case "show-declined-proposals":
    case "show-unavailable-proposals":
    case "show-quoted-search":
    case "open-search-filters":
    case "set-travel-origin":
    case "select-bookmarked-rfp-batch":
    case "change-search-dates":
    case "show-preferred-search": {
      const { eventId } = subject as EventActionSubject;
      assertIds(eventId);
      return withAction(EVENT_SOURCE_ACTION_PATHS[action](eventId), action);
    }
    case "upload-signed-contract":
    case "upload-final-folio": {
      // The upload picker is mounted on every Finances tab, so it opens on the tab given.
      const { eventId, tab = EVENT_FINANCES_UPLOAD_TABS[action] } = subject as EventFinancesTabActionSubject;
      assertIds(eventId);
      return withAction(adminEventFinancesPath(eventId, tab), action);
    }
    case "upload-venue-documents": {
      const { venueId, eventId } = subject as VenueActionSubject;
      assertIds(venueId, eventId);
      return withAction(`${adminEventFinancesPath(eventId, "documents")}?${VENUE_PARAM}=${venueId}`, action);
    }
    case "show-proposals-table": {
      const { eventId } = subject as EventActionSubject;
      assertIds(eventId);
      return `${adminEventSourcePath(eventId, "proposals")}?view=table`;
    }
    case "edit-venue-notes":
    case "manage-venue-photos":
    case "add-venue-email":
    case "negotiate-by-email":
    case "rescrape-venue-images":
      return withAction(venueOverlayPath("venues", subject as VenueActionSubject), action);
    case "message-venue":
    case "view-venue-emails": {
      // The overlay's message prompt and emails section are the same on both tabs.
      const { tab = "venues", ...venue } = subject as VenueOverlayTabActionSubject;
      return withAction(venueOverlayPath(tab, venue), action);
    }
    case "undecline-proposal":
    case "read-venue-notes":
    case "ask-venue-about-requirements":
      return withAction(venueOverlayPath("proposals", subject as VenueActionSubject), action);
    case "open-venue-website":
      // A plain link to the venue's overlay; the palette swaps in the venue's own website once its
      // facts have one (the overlay shows the same link).
      return venueOverlayPath("venues", subject as VenueActionSubject);
    case "compare-bookmarked-proposals": {
      const { eventId, venueIds = [] } = subject as ProposalCompareActionSubject;
      assertIds(eventId, ...venueIds);
      if (venueIds.length === 0) return adminEventSourcePath(eventId, "proposals");
      return `${adminEventProposalsComparePath(eventId)}?venues=${venueIds.join(",")}`;
    }
    case "view-proposal-quote": {
      const { quoteGroupId, ...venue } = subject as VenueQuoteActionSubject;
      const path = venueOverlayPath("proposals", venue);
      if (quoteGroupId === undefined) return withAction(path, action);
      if (!UUID_PATTERN.test(quoteGroupId)) throw new Error("adminActionLink: offer id must be a UUID");
      return withAction(`${path}&${OFFER_PARAM}=${quoteGroupId}`, action);
    }
    case "request-venue-proposal": {
      const { venueId, eventId } = subject as VenueActionSubject;
      assertIds(venueId, eventId);
      return withAction(`${adminEventSourcePath(eventId, "search")}/hotel?${VENUE_PARAM}=${venueId}`, action);
    }
    case "new-venue-note": {
      const { venueId, eventId } = subject as VenueActionSubject;
      assertIds(venueId, eventId);
      return withAction(`${adminEventNotesPath(eventId)}?${VENUE_PARAM}=${venueId}`, action);
    }
    case "request-contract-approval":
    case "comment-on-contract":
    case "export-contract":
    case "review-unmet-terms":
    case "approve-contract":
    case "rename-contract": {
      const { eventId, contractId } = subject as ContractActionSubject;
      assertIds(eventId, contractId);
      return withAction(adminEventContractPath(eventId, contractId), action);
    }
    default:
      throw new Error("adminActionLink: unknown action");
  }
}
